/**
 * Lexicon spreadsheet (.xlsx) build/parse — the translator's working file.
 *
 * One file per language, covering BOTH halves of the problem: the UI dictionary
 * strings from public/locales, and every piece of admin-authored database
 * content. A translator sees one job and sends back one file.
 *
 * Why .xlsx rather than CSV — the same reason the product tools use it: Excel
 * mangles CSV on open, before any edit. Here the specific hazards are leading
 * apostrophes, embedded commas and newlines in longer copy, and the `{{count}}`
 * interpolation braces. A real workbook with every cell typed as text
 * round-trips untouched.
 *
 * The REFERENCE column is the identity that makes re-import possible, and it is
 * locked: it is how a row finds its way back to a dictionary key or a database
 * field. Everything except TRANSLATION is reference material.
 */

import * as XLSX from 'xlsx';

/** One translatable string, as exported. */
export interface LexiconRow {
  /** 'ui' (dictionary string) or 'db' (admin-authored content). */
  source: string;
  /** Stable identity: 'results:bom.title' or 'products#12:name'. */
  ref: string;
  /** Where it appears, for the translator's benefit. Never round-tripped. */
  context: string;
  english: string;
  /** Whatever translation currently applies. Pre-filled for the translator. */
  current: string;
}

/**
 * One row to submit. `english` and `context` are what make a file portable
 * between environments: content references carry database ids, and the same
 * product or country has a different id on local, beta and production. The
 * server matches on the English text when the id does not line up. Both are
 * optional so a file with the reference columns stripped still imports.
 */
export interface LexiconImportRow {
  ref: string;
  value: string;
  english?: string;
  context?: string;
}

/** What comes back from a translator's file. */
export interface ParsedLexicon {
  rows: LexiconImportRow[];
  /** Rows whose REFERENCE was missing or unusable. */
  malformed: number;
  /** Headers we could not find — the file is probably not a lexicon export. */
  missingHeaders: string[];
}

const SHEET_NAME = 'Lexicon';

const HEADERS = {
  source: 'SOURCE',
  ref: 'REFERENCE',
  context: 'CONTEXT',
  english: 'ENGLISH',
  translation: 'TRANSLATION',
} as const;

/**
 * Build the workbook.
 *
 * Column widths are set because the default makes every column ~8 characters
 * wide, which renders a 200-character disclaimer as an unreadable sliver and is
 * the first thing a translator would have to fix by hand.
 */
export function buildLexiconWorkbook(rows: LexiconRow[], locale: string): Blob {
  const header = [HEADERS.source, HEADERS.ref, HEADERS.context, HEADERS.english, HEADERS.translation];

  const aoa: (string | number)[][] = [header];
  for (const r of rows) {
    aoa.push([r.source, r.ref, r.context, r.english, r.current]);
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Force every cell to text. Without this, a translation that happens to look
  // like a number or a date ("2026", "1/2") is silently retyped by Excel and
  // comes back as a serial number.
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let R = range.s.r; R <= range.e.r; R++) {
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = ws[addr];
      if (cell) { cell.t = 's'; cell.z = '@'; }
    }
  }

  ws['!cols'] = [
    { wch: 8 },   // SOURCE
    { wch: 46 },  // REFERENCE
    { wch: 28 },  // CONTEXT
    { wch: 70 },  // ENGLISH
    { wch: 70 },  // TRANSLATION
  ];
  ws['!freeze'] = { xSplit: '0', ySplit: '1' };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, SHEET_NAME);

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([out], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/** Trigger a browser download for a built workbook. */
export function downloadLexicon(rows: LexiconRow[], locale: string): void {
  const blob = buildLexiconWorkbook(rows, locale);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `lexicon-${locale}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Parse a translator's file back into rows to submit.
 *
 * Header-driven and order-independent: translators reorder, hide and re-sort
 * columns constantly, and a positional parser would read the wrong column
 * without ever failing. Only REFERENCE and TRANSLATION are required — a file
 * with the reference material stripped out is still importable.
 */
export async function parseLexiconFile(file: File): Promise<ParsedLexicon> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });

  // Prefer our own sheet name, but accept the first sheet: translators
  // sometimes copy rows into a fresh workbook.
  const sheet = wb.Sheets[SHEET_NAME] ?? wb.Sheets[wb.SheetNames[0]];
  if (!sheet) return { rows: [], malformed: 0, missingHeaders: [HEADERS.ref, HEADERS.translation] };

  const table = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
    header: 1,
    raw: false,
    defval: '',
  }) as unknown as string[][];

  if (table.length === 0) return { rows: [], malformed: 0, missingHeaders: [HEADERS.ref, HEADERS.translation] };

  const headerRow = table[0].map(h => String(h ?? '').trim().toUpperCase());
  const refCol = headerRow.indexOf(HEADERS.ref);
  const valCol = headerRow.indexOf(HEADERS.translation);

  const missingHeaders: string[] = [];
  if (refCol === -1) missingHeaders.push(HEADERS.ref);
  if (valCol === -1) missingHeaders.push(HEADERS.translation);
  if (missingHeaders.length) return { rows: [], malformed: 0, missingHeaders };

  // Optional: present in our own exports, and what lets the server place a row
  // whose id belongs to a different environment.
  const engCol = headerRow.indexOf(HEADERS.english);
  const ctxCol = headerRow.indexOf(HEADERS.context);

  const rows: LexiconImportRow[] = [];
  let malformed = 0;

  for (let i = 1; i < table.length; i++) {
    const ref = String(table[i]?.[refCol] ?? '').trim();
    if (ref === '') {
      // A wholly blank row is just spreadsheet padding, not an error.
      const anyContent = (table[i] ?? []).some(c => String(c ?? '').trim() !== '');
      if (anyContent) malformed++;
      continue;
    }
    const row: LexiconImportRow = { ref, value: String(table[i]?.[valCol] ?? '').trim() };
    if (engCol !== -1) row.english = String(table[i]?.[engCol] ?? '');
    if (ctxCol !== -1) row.context = String(table[i]?.[ctxCol] ?? '');
    rows.push(row);
  }

  return { rows, malformed, missingHeaders: [] };
}
